// @flow
import moment from 'moment-timezone';
import {Transforms} from '@wellstone-solutions/common';
import type Moment from 'moment';
import type {
  UIMemberType,
  UICalendarEventType,
} from '@wellstone-solutions/common';

export const isGroupStartDateValid = ({
  member,
  selectedStartDate,
  programId,
}: {
  member: UIMemberType,
  selectedStartDate: Moment,
  programId: string,
}): boolean => {
  const programAdmission = member.admissions.find(
    (admission) => admission.program.id === programId,
  );

  if (!programAdmission) {
    return false;
  }

  // $FlowIgnore - programAdmission type is wrong for start_date
  return selectedStartDate.isSameOrAfter(programAdmission.start_date, 'day');
};

export const toV2Api = (groupEvent: any): any => {
  const {
    // transform these for api dates
    endTime,
    startDate,
    startTime,
    recurrence,
    data,
    ...rest
  } = groupEvent;

  const keysToOmit = ['id', 'originalStart', 'group'];

  // remove invitations on updates (will always be non-empty due to owner)
  if (rest.invitations.length > 0) {
    keysToOmit.push('invitations');
  }

  const [endHour, endMinute] = endTime.split(':');
  const [startHour, startMinute] = startTime.split(':');

  const end = moment(startDate)
    .set({
      hour: Number(endHour),
      minute: Number(endMinute),
      second: 0,
      millisecond: 0,
    })
    .toISOString();
  const start = moment(startDate)
    .set({
      hour: Number(startHour),
      minute: Number(startMinute),
      second: 0,
      millisecond: 0,
    })
    .toISOString();

  // make sure these recurrence fields work for v2 group events API
  if (recurrence.byweekday === '') {
    recurrence.byweekday = null;
  }

  if (recurrence.bysetpos === 0) {
    recurrence.bysetpos = null;
  }

  // Clear location or link values based on event type
  if (data.eventType === 'Virtual') {
    data.location = {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
      name: '',
    };
    data.phone = '';
  } else {
    data.link = '';
  }

  return {
    ...Transforms.omitKeys(rest, keysToOmit),
    end,
    start,
    recurrence,
    data,
  };
};

export const toV2EventUI = (calendarEvent: Event): UICalendarEventType => {
  const {end, start, timezone, ...rest} = calendarEvent;

  const recurrence = calendarEvent.isRecurring ? calendarEvent.recurrence : {};

  // This handles both legacy events and group events
  // Future consideration: update useMonthlySelect to handle both cases
  if (recurrence.byweekday === null) {
    recurrence.byweekday = '';
  }

  for (const param in recurrence) {
    if (recurrence[param] === null) {
      delete recurrence[param];
    }
  }

  const startTime = moment.utc(start).tz(timezone).format('HH:mm');
  const endTime = moment.utc(end).tz(timezone).format('HH:mm');

  const result = {
    ...rest,
    recurrence,
    startDate: start,
    startTime,
    endTime,
    timezone,
  };

  return result;
};
